// cj-closeup-filter.js
// Hides per-color design closeup gallery items that do not match the selected color.
// Also filters the product-modal lightbox to show only the selected color's images.
// Keyed on CDN filename: 03_design_closeup_{design-slug}_{color_key}.jpg
// design-slug uses hyphens; color_key uses underscores — first underscore is the split.
// No Lambda changes required. Works on existing enriched products.

(function () {
  'use strict';

  // Module-level selected color — updated on every variant change
  var _currentColorKey = null;

  // Match Lambda's _color_key(): lower, spaces/hyphens/slashes → underscore
  function toColorKey(str) {
    return str.toLowerCase().replace(/[\s\-\/]+/g, '_');
  }

  // Extract the color_key from a Shopify CDN src containing a closeup filename.
  // Returns null if the src is not a design closeup image.
  // T135 — strips an optional trailing UUID suffix. Shopify's Files API
  // appends a UUID like `_522b262d-cfc2-41a1-9666-e6b211dd65f8` when a fresh
  // upload collides with an existing file in the Files library (orphans from
  // prior enrichment runs). Without stripping, heather colors with UUID-
  // suffixed filenames fail the equality check below and stay hidden when
  // the matching swatch is selected. Mirrors mockupColorKey()'s handling.
  function closeupColorKey(src) {
    const m = src.match(/03_design_closeup_([^?#&]+)\.jpg/i);
    if (!m) return null;
    // m[1] = "{design-slug}_{color_key}" possibly followed by "_{uuid}".
    // Strip the trailing UUID if present before splitting on the first
    // underscore (which separates the hyphenated design-slug from the
    // underscore-joined color_key).
    let inner = m[1].replace(
      /_[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i,
      ''
    );
    const idx = inner.indexOf('_');
    if (idx === -1) return null;
    return inner.slice(idx + 1);
  }

  // Extract the color_key from a mockup filename.
  // Handles UUID variant: mockup_{color_key}_{uuid}.jpg
  // And plain variant:    mockup_{color_key}.jpg
  // Returns null if the src is not a mockup image.
  function mockupColorKey(src) {
    if (!src) return null;
    const filename = src.split('/').pop().split('?')[0];
    // UUID variant first (more specific)
    let m = filename.match(
      /^mockup_(.+)_[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}\.jpg$/i
    );
    if (m) return m[1];
    // Plain variant
    m = filename.match(/^mockup_(.+)\.jpg$/i);
    return m ? m[1] : null;
  }

  function applyFilter(selectedColorKey) {
    _currentColorKey = selectedColorKey;
    if (!selectedColorKey) return;
    const gallery = document.querySelector('media-gallery');
    if (!gallery) return;

    // Main gallery viewer slides
    gallery.querySelectorAll('[id^="GalleryViewer"] li[data-media-id]').forEach((li) => {
      const img = li.querySelector('img');
      const ck = img ? closeupColorKey(img.src) : null;
      if (ck !== null) li.style.display = ck === selectedColorKey ? '' : 'none';
    });

    // Thumbnail strip
    gallery.querySelectorAll('[id^="GalleryThumbnails"] li.thumbnail-list__item').forEach((li) => {
      const img = li.querySelector('img');
      const ck = img ? closeupColorKey(img.src) : null;
      if (ck !== null) li.style.display = ck === selectedColorKey ? '' : 'none';
    });

    // T138 — Override the slider counter "of N" with the count of visible
    // items. SliderComponent's initPages() filters via `clientWidth > 0`,
    // but Dawn's product gallery uses a scroll-snap layout where only the
    // active slide has clientWidth > 0 (off-screen slides report 0 too),
    // so initPages always sees `< 2 visible` and early-returns without
    // updating totalPages. We sidestep that by writing the visible total
    // directly to `.slider-counter--total`. Counts only LI elements that
    // (a) are slides (have a data-media-id) and (b) are not display:none
    // (filtered via inline style above). Falls back gracefully if the
    // gallery markup ever changes (does nothing if no counter is found).
    const viewerLis = Array.from(
      gallery.querySelectorAll('[id^="GalleryViewer"] li[data-media-id]')
    );
    const visibleCount = viewerLis.filter((li) => li.style.display !== 'none').length;
    if (visibleCount > 0) {
      gallery.querySelectorAll('.slider-counter--total').forEach((el) => {
        el.textContent = visibleCount;
      });
      // T139 — Mobile fix. The desktop counter sits inside .slider-buttons
      // which Dawn hides on desktop (≥750px) via section-main-product.css.
      // Mobile (<750px) shows the counter AND uses a horizontal scroll-snap
      // layout where SliderComponent.update() fires on every scroll and
      // writes `this.totalPages` into `.slider-counter--total`, overwriting
      // the T138 write above. `this.totalPages` was computed by initPages()
      // on page load BEFORE our filter applied, so it equals the full
      // pre-filter LI count (e.g. 14). We patch it directly on the slider
      // instance so subsequent scroll-triggered update() calls preserve
      // our visible count. Safe on desktop too: totalPages is unused there
      // because update() never fires (no horizontal scroll on the vertical
      // gallery), and even if a ResizeObserver tick later re-runs initPages,
      // the next variantChange will restore the right value via this same
      // path. Also set sliderItemsToShow so update()'s sliderItemOffset
      // arithmetic doesn't divide-by-zero on the first scroll.
      const sliderComp = gallery.querySelector('slider-component');
      if (sliderComp) {
        sliderComp.totalPages = visibleCount;
      }
    }
  }

  // Filter modal lightbox images to show only those matching the selected color.
  // Shared images (lifestyle, color grid, brand story) have no color key → always shown.
  function applyModalFilter(selectedColorKey) {
    if (!selectedColorKey) return;
    document.querySelectorAll('.product-media-modal__content img').forEach((img) => {
      const ck = closeupColorKey(img.src) || mockupColorKey(img.src);
      if (ck !== null) img.style.display = ck === selectedColorKey ? '' : 'none';
    });
  }

  // Watch for product-modal gaining the 'open' attribute and apply the filter.
  function watchModal() {
    const productModal = document.querySelector('product-modal');
    if (!productModal) return;
    new MutationObserver((mutations) => {
      for (const mut of mutations) {
        if (mut.attributeName === 'open' && productModal.hasAttribute('open')) {
          applyModalFilter(_currentColorKey);
        }
      }
    }).observe(productModal, { attributes: true, attributeFilter: ['open'] });
  }

  function initialColorKey() {
    // Prefer the selected variant JSON that Dawn embeds in the DOM
    const el = document.querySelector('[data-selected-variant]');
    if (el) {
      try {
        const v = JSON.parse(el.textContent || el.innerHTML || '{}');
        if (v && v.option1) return toColorKey(v.option1);
      } catch (_) {}
    }
    // Fallback: first checked color radio
    const radio = document.querySelector(
      'input[type="radio"]:checked[name="Color"], input[type="radio"]:checked[name="color"]'
    );
    if (radio) return toColorKey(radio.value);
    return null;
  }

  document.addEventListener('DOMContentLoaded', () => {
    // Apply on initial page load
    applyFilter(initialColorKey());

    // Watch for modal opens
    watchModal();

    // Re-apply after Dawn's variant change cycle (fires after gallery DOM is updated)
    if (typeof subscribe !== 'undefined' && typeof PUB_SUB_EVENTS !== 'undefined') {
      subscribe(PUB_SUB_EVENTS.variantChange, ({ data }) => {
        if (data && data.variant && data.variant.option1) {
          applyFilter(toColorKey(data.variant.option1));
        }
      });
    }
  });
})();
